import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Users,
  Camera,
  Upload,
  BookOpen,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  createStudent,
  updateStudent,
  getStudentById,
} from "../../services/students";
import { getCourses } from "../../services/courses";
import {
  getDepartmentsByBranch,
  getDepartments,
} from "../../services/departments";
import api from "../../services/api";
import CameraModal from "../modals/CameraModal";

// Country codes data
const COUNTRY_CODES = [
  { code: "+254", name: "Kenya", flag: "🇰🇪" },
  { code: "+1", name: "United States", flag: "🇺🇸" },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
  { code: "+91", name: "India", flag: "🇮🇳" },
  { code: "+255", name: "Tanzania", flag: "🇹🇿" },
  { code: "+256", name: "Uganda", flag: "🇺🇬" },
  { code: "+27", name: "South Africa", flag: "🇿🇦" },
  { code: "+20", name: "Egypt", flag: "🇪🇬" },
  { code: "+234", name: "Nigeria", flag: "🇳🇬" },
  { code: "+233", name: "Ghana", flag: "🇬🇭" },
  { code: "+225", name: "Ivory Coast", flag: "🇨🇮" },
  { code: "+212", name: "Morocco", flag: "🇲🇦" },
  { code: "+213", name: "Algeria", flag: "🇩🇿" },
  { code: "+216", name: "Tunisia", flag: "🇹🇳" },
  { code: "+218", name: "Libya", flag: "🇱🇾" },
  { code: "+220", name: "Gambia", flag: "🇬🇲" },
  { code: "+221", name: "Senegal", flag: "🇸🇳" },
  { code: "+222", name: "Mauritania", flag: "🇲🇷" },
  { code: "+223", name: "Mali", flag: "🇲🇱" },
  { code: "+224", name: "Guinea", flag: "🇬🇳" },
  { code: "+226", name: "Burkina Faso", flag: "🇧🇫" },
  { code: "+228", name: "Togo", flag: "🇹🇬" },
  { code: "+229", name: "Benin", flag: "🇧🇯" },
  { code: "+230", name: "Mauritius", flag: "🇲🇺" },
  { code: "+231", name: "Liberia", flag: "🇱🇷" },
  { code: "+232", name: "Sierra Leone", flag: "🇸🇱" },
  { code: "+235", name: "Chad", flag: "🇹🇩" },
  { code: "+236", name: "Central African Republic", flag: "🇨🇫" },
  { code: "+237", name: "Cameroon", flag: "🇨🇲" },
  { code: "+238", name: "Cape Verde", flag: "🇨🇻" },
  { code: "+239", name: "São Tomé and Príncipe", flag: "🇸🇹" },
  { code: "+240", name: "Equatorial Guinea", flag: "🇬🇶" },
  { code: "+241", name: "Gabon", flag: "🇬🇦" },
  { code: "+242", name: "Republic of the Congo", flag: "🇨🇬" },
  { code: "+243", name: "Democratic Republic of the Congo", flag: "🇨🇩" },
  { code: "+244", name: "Angola", flag: "🇦🇴" },
  { code: "+245", name: "Guinea-Bissau", flag: "🇬🇼" },
  { code: "+246", name: "British Indian Ocean Territory", flag: "🇮🇴" },
  { code: "+248", name: "Seychelles", flag: "🇸🇨" },
  { code: "+249", name: "Sudan", flag: "🇸🇩" },
  { code: "+250", name: "Rwanda", flag: "🇷🇼" },
  { code: "+251", name: "Ethiopia", flag: "🇪🇹" },
  { code: "+252", name: "Somalia", flag: "🇸🇴" },
  { code: "+253", name: "Djibouti", flag: "🇩🇯" },
  { code: "+257", name: "Burundi", flag: "🇧🇮" },
  { code: "+258", name: "Mozambique", flag: "🇲🇿" },
  { code: "+260", name: "Zambia", flag: "🇿🇲" },
  { code: "+261", name: "Madagascar", flag: "🇲🇬" },
  { code: "+262", name: "Réunion", flag: "🇷🇪" },
  { code: "+263", name: "Zimbabwe", flag: "🇿🇼" },
  { code: "+264", name: "Namibia", flag: "🇳🇦" },
  { code: "+265", name: "Malawi", flag: "🇲🇼" },
  { code: "+266", name: "Lesotho", flag: "🇱🇸" },
  { code: "+267", name: "Botswana", flag: "🇧🇼" },
  { code: "+268", name: "Eswatini", flag: "🇸🇿" },
  { code: "+269", name: "Comoros", flag: "🇰🇲" },
  { code: "+290", name: "Saint Helena", flag: "🇸🇭" },
  { code: "+291", name: "Eritrea", flag: "🇪🇷" },
  { code: "+297", name: "Aruba", flag: "🇦🇼" },
  { code: "+298", name: "Faroe Islands", flag: "🇫🇴" },
  { code: "+299", name: "Greenland", flag: "🇬🇱" },
];

// Phone input component with country code
const PhoneInput: React.FC<{
  label: string;
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (code: string) => void;
  onPhoneNumberChange: (number: string) => void;
  required?: boolean;
  placeholder?: string;
}> = ({
  label,
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  required = false,
  placeholder = "Phone number",
}) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="flex">
        <select
          value={countryCode}
          onChange={(e) => onCountryCodeChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 min-w-[120px]"
        >
          {COUNTRY_CODES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.code}
            </option>
          ))}
        </select>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => onPhoneNumberChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="flex-1 px-3 py-2 border-l-0 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
};

interface StudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId?: string; // If provided, we're editing; otherwise, we're creating
}

interface StudentFormData {
  // User/Personal Information
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  dateOfBirth: string;
  gender: "male" | "female" | "other";
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };

  // Student-specific Information
  currentClassId: string;
  departmentId: string; // Required department assignment
  enrollmentDate: string;
  referralSource?: {
    source:
      | "banner"
      | "billboard"
      | "flyers"
      | "social_media"
      | "friend"
      | "family"
      | "other";
    otherDescription?: string;
  };
  academicStatus:
    | "active"
    | "inactive"
    | "suspended"
    | "graduated"
    | "transferred"
    | "dropped";

  // Course Assignment
  courses: string[]; // Array of course IDs

  // Discount/Scholarship Information
  discountPercentage?: number; // Optional discount (0-100)
  discountReason?: string; // Optional reason for discount

  // Photo Information
  photo?: File | null;
  photoPreview?: string;
  existingPhotoUrl?: string; // For editing existing student

  // Parent/Guardian Information
  parentGuardianInfo: {
    fatherName: string;
    motherName: string;
    guardianName: string;
    guardianPhone: string;
    guardianEmail: string;
    emergencyContact: {
      name: string;
      phone: string;
      email: string;
      relationship: string;
    };
  };
}

// Draft persistence (only used for the "create new student" flow, not editing)
const STUDENT_DRAFT_STORAGE_KEY = "cms:studentRegistration:draft";
const STUDENT_DRAFT_VERSION = 1;
const STUDENT_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// File/preview/server-derived fields can't (or shouldn't) be persisted to localStorage
const STUDENT_DRAFT_EXCLUDED_KEYS: (keyof StudentFormData)[] = [
  "photo",
  "photoPreview",
  "existingPhotoUrl",
];

interface StudentDraftPayload {
  version: number;
  savedAt: string;
  data: Partial<StudentFormData>;
}

const saveStudentDraft = (formData: StudentFormData) => {
  try {
    const data: Partial<StudentFormData> = { ...formData };
    STUDENT_DRAFT_EXCLUDED_KEYS.forEach((key) => delete data[key]);
    const payload: StudentDraftPayload = {
      version: STUDENT_DRAFT_VERSION,
      savedAt: new Date().toISOString(),
      data,
    };
    localStorage.setItem(STUDENT_DRAFT_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn("Failed to save student registration draft:", error);
  }
};

const loadStudentDraft = (): StudentDraftPayload | null => {
  try {
    const raw = localStorage.getItem(STUDENT_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw) as StudentDraftPayload;
    if (
      !payload ||
      payload.version !== STUDENT_DRAFT_VERSION ||
      !payload.data ||
      !payload.savedAt
    ) {
      return null;
    }
    const age = Date.now() - new Date(payload.savedAt).getTime();
    if (Number.isNaN(age) || age > STUDENT_DRAFT_MAX_AGE_MS) {
      return null;
    }
    return payload;
  } catch (error) {
    console.warn("Failed to load student registration draft:", error);
    return null;
  }
};

const clearStudentDraft = () => {
  try {
    localStorage.removeItem(STUDENT_DRAFT_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear student registration draft:", error);
  }
};

const StudentModal: React.FC<StudentModalProps> = ({
  isOpen,
  onClose,
  studentId,
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!studentId;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);

  // Image cropping state
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [crop, setCrop] = useState<Crop>({
    unit: "px",
    width: 200,
    height: 200,
    x: 50,
    y: 50,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [imageSrc, setImageSrc] = useState<string>("");
  const imgRef = useRef<HTMLImageElement>(null);

  // Info about a restored "continue later" draft, shown as a dismissible banner
  const [draftInfo, setDraftInfo] = useState<{ savedAt: string } | null>(null);

  const [formData, setFormData] = useState<StudentFormData>(() => ({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    dateOfBirth: "",
    gender: "male",
    phone: "",
    phoneCountryCode: "+254", // Kenya default
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "Kenya",
    },
    currentClassId: "",
    departmentId: "",
    enrollmentDate: new Date().toISOString().split("T")[0],
    referralSource: {
      source: "social_media",
      otherDescription: "",
    },
    academicStatus: "active",
    courses: [], // Initialize as empty array
    discountPercentage: 0, // Initialize discount
    discountReason: "", // Initialize discount reason
    discountAmount: 0, // Initialize discount amount
    photo: null,
    photoPreview: "",
    existingPhotoUrl: "",
    parentGuardianInfo: {
      fatherName: "",
      motherName: "",
      guardianName: "",
      guardianPhone: "",
      guardianPhoneCountryCode: "+254", // Kenya default
      guardianEmail: "",
      emergencyContact: {
        name: "",
        phone: "",
        phoneCountryCode: "+254", // Kenya default
        email: "",
        relationship: "Guardian",
      },
    },
  }));

  // Reset form when modal opens/closes or studentId changes
  useEffect(() => {
    if (!isOpen || (!isEditing && !studentId)) {
      const blankFormData = {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        dateOfBirth: "",
        gender: "male",
        phone: "",
        phoneCountryCode: "+254", // Kenya default
        address: {
          street: "",
          city: "",
          state: "",
          zipCode: "",
          country: "Kenya",
        },
        currentClassId: "",
        enrollmentDate: new Date().toISOString().split("T")[0],
        referralSource: {
          source: "social_media",
          otherDescription: "",
        },
        academicStatus: "active",
        courses: [], // Initialize as empty array
        discountPercentage: 0, // Initialize discount
        discountReason: "", // Initialize discount reason
        discountAmount: 0, // Initialize discount amount
        photo: null,
        photoPreview: "",
        existingPhotoUrl: "",
        parentGuardianInfo: {
          fatherName: "",
          motherName: "",
          guardianName: "",
          guardianPhone: "",
          guardianPhoneCountryCode: "+254", // Kenya default
          guardianEmail: "",
          emergencyContact: {
            name: "",
            phone: "",
            phoneCountryCode: "+254", // Kenya default
            email: "",
            relationship: "Guardian",
          },
        },
      } as unknown as StudentFormData;

      // Only offer a saved draft when opening the "create new student" form
      if (isOpen && !isEditing) {
        const draft = loadStudentDraft();
        if (draft) {
          setFormData({ ...blankFormData, ...draft.data });
          setDraftInfo({ savedAt: draft.savedAt });
          return;
        }
      }
      setDraftInfo(null);
      setFormData(blankFormData);
    }
  }, [isOpen, isEditing, studentId]);

  // Autosave the in-progress "create new student" form so it can be resumed later
  useEffect(() => {
    if (!isOpen || isEditing) return;
    const timeoutId = setTimeout(() => {
      saveStudentDraft(formData);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData, isOpen, isEditing]);

  const handleDiscardDraft = () => {
    clearStudentDraft();
    setDraftInfo(null);
    setFormData((prev) => ({
      ...prev,
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      dateOfBirth: "",
      gender: "male",
      phone: "",
      phoneCountryCode: "+254",
      address: { street: "", city: "", state: "", zipCode: "", country: "Kenya" },
      currentClassId: "",
      departmentId: "",
      enrollmentDate: new Date().toISOString().split("T")[0],
      referralSource: { source: "social_media", otherDescription: "" },
      academicStatus: "active",
      courses: [],
      discountPercentage: 0,
      discountReason: "",
      discountAmount: 0,
      photo: null,
      photoPreview: "",
      existingPhotoUrl: "",
      parentGuardianInfo: {
        fatherName: "",
        motherName: "",
        guardianName: "",
        guardianPhone: "",
        guardianPhoneCountryCode: "+254",
        guardianEmail: "",
        emergencyContact: {
          name: "",
          phone: "",
          phoneCountryCode: "+254",
          email: "",
          relationship: "Guardian",
        },
      },
    }));
  };

  // Fetch student data for editing
  const { data: studentData, isLoading: isLoadingStudent } = useQuery({
    queryKey: ["student", studentId],
    queryFn: () => getStudentById(studentId!),
    enabled: isEditing && !!studentId,
  });

  // Fetch classes for dropdown
  const { data: classesData, isLoading: isLoadingClasses } = useQuery({
    queryKey: ["classes"],
    queryFn: async () => {
      const response = await api.get("/classes?isActive=true&limit=100");
      return response;
    },
  });

  // Fetch courses for dropdown
  const { data: coursesData, isLoading: isLoadingCourses } = useQuery({
    queryKey: ["courses"],
    queryFn: async () => {
      const response = await getCourses({ limit: 100, page: 1 });
      return response;
    },
  });

  // Fetch departments for dropdown
  const { data: departmentsData, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => {
      const response = await getDepartments(1, 100, undefined, undefined, true);
      return response;
    },
  });

  // Photo handling functions
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error("Photo size should be less than 5MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setImageSrc(result);
        setCrop({
          unit: "px",
          width: 200,
          height: 200,
          x: 50,
          y: 50,
        }); // Reset to initial crop
        setCompletedCrop(undefined);
        setIsCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setFormData((prev) => ({
      ...prev,
      photo: null,
      photoPreview: "",
      existingPhotoUrl: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleCameraCapture = (imageData: string) => {
    // Convert base64 data URL to File object
    const byteString = atob(imageData.split(",")[1]);
    const mimeString = imageData.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], "camera-photo.jpg", { type: "image/jpeg" });

    // Show crop modal instead of directly setting
    setImageSrc(imageData);
    setCrop({
      unit: "px",
      width: 200,
      height: 200,
      x: 50,
      y: 50,
    }); // Reset to initial crop
    setCompletedCrop(undefined);
    setIsCropModalOpen(true);
  };

  // Image cropping functions
  const getCroppedImg = (
    image: HTMLImageElement,
    crop: PixelCrop,
    fileName: string,
  ): Promise<Blob | null> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return Promise.reject(new Error("No 2d context"));
    }

    // Use device pixel ratio for high quality
    const pixelRatio = window.devicePixelRatio || 1;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.scale(pixelRatio, pixelRatio);

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.95,
      );
    });
  };

  const handleCropComplete = async () => {
    if (
      !completedCrop ||
      !imgRef.current ||
      completedCrop.width === 0 ||
      completedCrop.height === 0
    ) {
      toast.error("Please select a crop area first");
      return;
    }

    try {
      const croppedBlob = await getCroppedImg(
        imgRef.current,
        completedCrop,
        "cropped-image.jpg",
      );

      if (croppedBlob) {
        const croppedFile = new File([croppedBlob], "cropped-photo.jpg", {
          type: "image/jpeg",
        });

        const croppedUrl = URL.createObjectURL(croppedBlob);

        setFormData((prev) => ({
          ...prev,
          photo: croppedFile,
          photoPreview: croppedUrl,
        }));

        setIsCropModalOpen(false);
        setImageSrc("");
        setCrop(undefined);
        setCompletedCrop(undefined);

        toast.success("Image cropped successfully!");
      } else {
        throw new Error("Failed to create cropped image");
      }
    } catch (error) {
      console.error("Error cropping image:", error);
      toast.error("Failed to crop image");
    }
  };

  const handleCropCancel = () => {
    setIsCropModalOpen(false);
    setImageSrc("");
    setCrop({
      unit: "px",
      width: 200,
      height: 200,
      x: 50,
      y: 50,
    });
    setCompletedCrop(undefined);
  }; // Course handling functions

  // Course handling functions
  const handleCourseSelect = (courseId: string) => {
    setFormData((prev) => ({
      ...prev,
      courses: prev.courses.includes(courseId)
        ? prev.courses.filter((id) => id !== courseId)
        : [...prev.courses, courseId],
    }));
  };

  const removeCourse = (courseId: string) => {
    setFormData((prev) => ({
      ...prev,
      courses: prev.courses.filter((id) => id !== courseId),
    }));
  };

  // Create student mutation
  const createMutation = useMutation({
    mutationFn: createStudent,
    onSuccess: (data) => {
      // Check if the operation was actually successful
      if (data.success) {
        toast.success("Student created successfully!");
        queryClient.invalidateQueries({ queryKey: ["students"] });
        onClose();
      } else {
        // Treat as an error if success is false
        console.error("Student creation failed:", data);
        toast.error(data.message || "Failed to create student");
      }
    },
    onError: (error: any) => {
      console.error("Error creating student:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to create student",
      );
    },
  });

  // Update student mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateStudent(id, data),
    onSuccess: (data) => {
      // Check if the operation was actually successful
      if (data.success) {
        toast.success("Student updated successfully!");
        queryClient.invalidateQueries({ queryKey: ["students"] });
        queryClient.invalidateQueries({ queryKey: ["student", studentId] });
        onClose();
      } else {
        // Treat as an error if success is false
        console.error("Student update failed:", data);
        toast.error(data.message || "Failed to update student");
      }
    },
    onError: (error: any) => {
      console.error("Error updating student:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update student",
      );
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (isEditing && studentData?.success && studentData.data) {
      const student = studentData.data as any;
      const user = student.userId || student.userInfo;

      // Helper function to extract country code and phone number
      const parsePhoneNumber = (fullPhone: string) => {
        if (!fullPhone) return { countryCode: "+254", phoneNumber: "" };

        // Try to match common country codes
        for (const country of COUNTRY_CODES) {
          if (fullPhone.startsWith(country.code)) {
            return {
              countryCode: country.code,
              phoneNumber: fullPhone.slice(country.code.length),
            };
          }
        }

        // Default fallback
        return { countryCode: "+254", phoneNumber: fullPhone };
      };

      const studentPhone = parsePhoneNumber(user?.profileDetails?.phone || "");
      const guardianPhone = parsePhoneNumber(
        student.parentGuardianInfo?.guardianPhone || "",
      );
      const emergencyPhone = parsePhoneNumber(
        student.parentGuardianInfo?.emergencyContact?.phone || "",
      );

      setFormData({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        dateOfBirth: user?.profileDetails?.dateOfBirth
          ? new Date(user.profileDetails.dateOfBirth)
              .toISOString()
              .split("T")[0]
          : "",
        gender: user?.profileDetails?.gender || "male",
        phone: studentPhone.phoneNumber,
        phoneCountryCode: studentPhone.countryCode,
        address: {
          street: user?.profileDetails?.address?.street || "",
          city: user?.profileDetails?.address?.city || "",
          state: user?.profileDetails?.address?.state || "",
          zipCode: user?.profileDetails?.address?.zipCode || "",
          country: user?.profileDetails?.address?.country || "Kenya",
        },
        currentClassId:
          student.currentClassId?._id || student.currentClassId || "",
        departmentId: student.departmentId || "",
        enrollmentDate: student.enrollmentDate
          ? new Date(student.enrollmentDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        referralSource: {
          source: student.referralSource?.source || "social_media",
          otherDescription: student.referralSource?.otherDescription || "",
        },
        academicStatus: student.academicStatus || "active",
        courses:
          student.courses?.map((course: any) => course._id || course) || [],
        photo: null,
        photoPreview: "",
        existingPhotoUrl:
          student.photoUrl || user?.profileDetails?.profilePicture || "",
        parentGuardianInfo: {
          fatherName: student.parentGuardianInfo?.fatherName || "",
          motherName: student.parentGuardianInfo?.motherName || "",
          guardianName: student.parentGuardianInfo?.guardianName || "",
          guardianPhone: guardianPhone.phoneNumber,
          guardianPhoneCountryCode: guardianPhone.countryCode,
          guardianEmail: student.parentGuardianInfo?.guardianEmail || "",
          emergencyContact: {
            name: student.parentGuardianInfo?.emergencyContact?.name || "",
            phone: emergencyPhone.phoneNumber,
            phoneCountryCode: emergencyPhone.countryCode,
            email: student.parentGuardianInfo?.emergencyContact?.email || "",
            relationship:
              student.parentGuardianInfo?.emergencyContact?.relationship ||
              "Guardian",
          },
        },
      });
    }
  }, [isEditing, studentData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission - check at the very start
    if (isSubmitting) {
      return;
    }

    // Enhanced validation
    const errors: string[] = [];

    // Required field validation
    if (!formData.firstName.trim()) {
      errors.push("First name is required");
    }
    if (!formData.lastName.trim()) {
      errors.push("Last name is required");
    }
    if (!formData.email.trim()) {
      errors.push("Email is required");
    }
    if (!isEditing && !formData.password) {
      errors.push("Password is required for new students");
    }
    if (!isEditing && formData.password && formData.password.length < 6) {
      errors.push("Password must be at least 6 characters long");
    }

    // Course and fee validation
    if (formData.courses.length === 0) {
      errors.push("At least one course must be assigned to the student");
    }

    // Emergency contact validation (required by backend)
    if (!formData.parentGuardianInfo.emergencyContact.name.trim()) {
      errors.push("Emergency contact name is required");
    }
    if (!formData.parentGuardianInfo.emergencyContact.phone.trim()) {
      errors.push("Emergency contact phone is required");
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.push("Please enter a valid email address");
    }
    if (
      formData.parentGuardianInfo.guardianEmail &&
      !emailRegex.test(formData.parentGuardianInfo.guardianEmail)
    ) {
      errors.push("Please enter a valid guardian email address");
    }
    if (
      formData.parentGuardianInfo.emergencyContact.email &&
      !emailRegex.test(formData.parentGuardianInfo.emergencyContact.email)
    ) {
      errors.push("Please enter a valid emergency contact email address");
    }

    // Show validation errors
    if (errors.length > 0) {
      toast.error(errors.join(", "));
      return;
    }

    try {
      let photoUrl = formData.existingPhotoUrl || "";

      // Upload photo if a new one is selected
      if (formData.photo) {
        const photoFormData = new FormData();
        photoFormData.append("photo", formData.photo);

        try {
          const photoResponse = await api.post("/upload/photo", photoFormData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          if (
            (photoResponse as any).success &&
            (photoResponse as any).data.url
          ) {
            photoUrl = (photoResponse as any).data.url;
          }
        } catch (photoError) {
          console.error("Error uploading photo:", photoError);
          toast.error(
            "Failed to upload photo, but continuing with student registration",
          );
        }
      }

      const submitData = {
        // User data
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        ...(formData.password && { password: formData.password }),
        profileDetails: {
          dateOfBirth: formData.dateOfBirth,
          gender: formData.gender,
          phone: formData.phone
            ? `${formData.phoneCountryCode}${formData.phone}`
            : "",
          address: formData.address,
          ...(photoUrl && { profilePicture: photoUrl }),
        },
        // Student data
        currentClassId: formData.currentClassId || undefined,
        departmentId: formData.departmentId || undefined,
        enrollmentDate: formData.enrollmentDate,
        referralSource: formData.referralSource,
        academicStatus: formData.academicStatus,
        courses: formData.courses, // Include course assignments
        photoUrl, // Always include photoUrl to allow removing photos
        parentGuardianInfo: {
          fatherName: formData.parentGuardianInfo.fatherName,
          motherName: formData.parentGuardianInfo.motherName,
          guardianName: formData.parentGuardianInfo.guardianName,
          guardianPhone: formData.parentGuardianInfo.guardianPhone
            ? `${formData.parentGuardianInfo.guardianPhoneCountryCode}${formData.parentGuardianInfo.guardianPhone}`
            : "",
          guardianEmail: formData.parentGuardianInfo.guardianEmail,
          emergencyContact: {
            name: formData.parentGuardianInfo.emergencyContact.name,
            phone: formData.parentGuardianInfo.emergencyContact.phone
              ? `${formData.parentGuardianInfo.emergencyContact.phoneCountryCode}${formData.parentGuardianInfo.emergencyContact.phone}`
              : "",
            email: formData.parentGuardianInfo.emergencyContact.email,
            relationship:
              formData.parentGuardianInfo.emergencyContact.relationship,
          },
        },
        // Discount/Scholarship data
        ...(formData.discountAmount &&
          formData.discountAmount > 0 && {
            discountPercentage: formData.discountPercentage,
            discountReason:
              formData.discountReason || "Discount applied during registration",
          }),
      } as any;

      // Set submitting state
      setIsSubmitting(true);
      try {
        if (isEditing) {
          await updateStudent(studentId!, submitData);
          toast.success("Student updated successfully");
          queryClient.invalidateQueries({ queryKey: ["students"] });
          onClose();
        } else {
          await createStudent(submitData);
          toast.success("Student created successfully");
          clearStudentDraft();
          setDraftInfo(null);
          queryClient.invalidateQueries({ queryKey: ["students"] });
          onClose();
        }
      } catch (error: any) {
        toast.error(
          error.response?.data?.message || "An unexpected error occurred",
        );
      } finally {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error("An unexpected error occurred");
    }
  };

  const handleChange = (field: string, value: any) => {
    const keys = field.split(".");
    if (keys.length === 1) {
      setFormData((prev) => ({ ...prev, [field]: value }));
    } else if (keys.length === 2) {
      setFormData((prev) => ({
        ...prev,
        [keys[0]]: {
          ...(prev[keys[0] as keyof typeof prev] as any),
          [keys[1]]: value,
        },
      }));
    } else if (keys.length === 3) {
      setFormData((prev) => ({
        ...prev,
        [keys[0]]: {
          ...(prev[keys[0] as keyof typeof prev] as any),
          [keys[1]]: {
            ...(prev[keys[0] as keyof typeof prev] as any)[keys[1]],
            [keys[2]]: value,
          },
        },
      }));
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to calculate course fee from new feeStructure model
  const calculateCourseFee = (course: any): number => {
    // Prioritize new feeStructure model
    if (
      course?.feeStructure?.components &&
      Array.isArray(course.feeStructure.components)
    ) {
      return course.feeStructure.components.reduce(
        (sum: number, comp: any) => sum + (comp.amount || 0),
        0,
      );
    }
    // Fallback to legacy fees model (but this is what we want to avoid)
    return course?.fees?.totalFee || 0;
  };

  // Calculate total fee
  const totalFee = useMemo(() => {
    return formData.courses.reduce((sum, courseId) => {
      const course = coursesData?.data?.find((c) => c._id === courseId);
      return sum + calculateCourseFee(course);
    }, 0);
  }, [formData.courses, coursesData]);

  if (!isOpen) return null;

  const isLoading =
    (createMutation.isPending || updateMutation.isPending || isSubmitting) &&
    !isLoadingStudent;
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 z-10">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <User className="h-6 w-6 text-blue-600" />
              {isEditing ? "Edit Student" : "Add New Student"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {!isEditing && draftInfo && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">
              <span>
                Resumed your unsaved draft from{" "}
                {new Date(draftInfo.savedAt).toLocaleString()}. Photo
                selection was not saved and needs to be re-added.
              </span>
              <button
                type="button"
                onClick={handleDiscardDraft}
                className="whitespace-nowrap font-medium text-blue-700 underline hover:text-blue-900"
              >
                Discard draft
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information Section */}
            <div className="border-b border-gray-200 pb-6">
              <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-4 w-4" />
                Personal Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    Email *
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    required
                  />
                </div>

                {!isEditing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      required={!isEditing}
                      minLength={6}
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.dateOfBirth}
                    onChange={(e) =>
                      handleChange("dateOfBirth", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.gender}
                    onChange={(e) =>
                      handleChange(
                        "gender",
                        e.target.value as "male" | "female" | "other",
                      )
                    }
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <PhoneInput
                    label="Phone"
                    countryCode={formData.phoneCountryCode}
                    phoneNumber={formData.phone}
                    onCountryCodeChange={(code) =>
                      handleChange("phoneCountryCode", code)
                    }
                    onPhoneNumberChange={(number) =>
                      handleChange("phone", number)
                    }
                    placeholder="712345678"
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="border-b border-gray-200 pb-6">
              <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Address Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.address.street}
                    onChange={(e) =>
                      handleChange("address.street", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.address.city}
                    onChange={(e) =>
                      handleChange("address.city", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State/County
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.address.state}
                    onChange={(e) =>
                      handleChange("address.state", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.address.zipCode}
                    onChange={(e) =>
                      handleChange("address.zipCode", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.address.country}
                    onChange={(e) =>
                      handleChange("address.country", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Academic Information Section */}
            <div className="border-b border-gray-200 pb-6">
              <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Academic Information
              </h4>

              {/* Photo Upload Section */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Student Photo
                </label>
                <div className="flex items-start gap-4">
                  {/* Photo Preview */}
                  <div className="flex-shrink-0">
                    {formData.photoPreview || formData.existingPhotoUrl ? (
                      <div className="relative">
                        <img
                          src={
                            formData.photoPreview || formData.existingPhotoUrl
                          }
                          alt="Student"
                          className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={removePhoto}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                        <Camera className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Upload Controls */}
                  <div className="flex-1">
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setIsCameraModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      >
                        <Camera className="h-4 w-4" />
                        Take Photo
                      </button>
                      <button
                        type="button"
                        onClick={triggerFileInput}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <Upload className="h-4 w-4" />
                        Upload Photo
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Recommended: 300x300px or higher. Max file size: 5MB.
                      Supported formats: JPG, PNG, GIF
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoSelect}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>

              {/* Course Selection Section */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Assign Courses
                </label>

                {/* Selected Courses */}
                {formData.courses.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {formData.courses.map((courseId) => {
                        const course = coursesData?.data?.find(
                          (c: any) => c._id === courseId,
                        );
                        return (
                          <span
                            key={courseId}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                          >
                            {course?.name || "Unknown Course"}({course?.level})
                            <button
                              type="button"
                              onClick={() => removeCourse(courseId)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Course Selection Dropdown */}
                <div className="relative">
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleCourseSelect(e.target.value);
                        e.target.value = ""; // Reset selection
                      }
                    }}
                    disabled={isLoadingCourses}
                  >
                    <option value="">Select courses to assign...</option>
                    {(() => {
                      // Handle different possible response structures for courses
                      let courses = [];
                      const response = coursesData as any;
                      if (response?.data) {
                        courses = response.data;
                      } else if (Array.isArray(response)) {
                        courses = response;
                      }

                      return courses
                        .filter(
                          (course: any) =>
                            !formData.courses.includes(course._id),
                        )
                        .map((course: any) => (
                          <option key={course._id} value={course._id}>
                            {course.name}({course.level})
                          </option>
                        ));
                    })()}
                  </select>
                  {isLoadingCourses && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select multiple courses that this student will be enrolled in
                </p>
              </div>

              {/* Fee Calculation Display */}
              {formData.courses.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h5 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                    💰 Course Fee Summary
                  </h5>
                  <div className="space-y-1">
                    {formData.courses.map((courseId) => {
                      const course = coursesData?.data?.find(
                        (c: any) => c._id === courseId,
                      );
                      const courseFee = calculateCourseFee(course);
                      return (
                        <div
                          key={courseId}
                          className="flex justify-between text-sm"
                        >
                          <span className="text-gray-700">
                            {course?.name || "Unknown Course"}({course?.level})
                          </span>
                          <span className="font-medium text-gray-900">
                            KSh {courseFee.toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                    <div className="border-t border-blue-300 pt-2 mt-2">
                      <div className="flex justify-between text-sm font-semibold">
                        <span className="text-blue-900">Total Fee:</span>
                        <span className="text-blue-900">
                          KSh{" "}
                          {(() => {
                            const total = formData.courses.reduce(
                              (sum, courseId) => {
                                const course = coursesData?.data?.find(
                                  (c: any) => c._id === courseId,
                                );
                                return sum + calculateCourseFee(course);
                              },
                              0,
                            );
                            return total.toLocaleString();
                          })()}
                        </span>
                      </div>
                      {formData.discountAmount &&
                        formData.discountAmount > 0 && (
                          <>
                            <div className="flex justify-between text-sm text-purple-600 mt-1">
                              <span>
                                Discount (KSh{" "}
                                {formData.discountAmount.toLocaleString()}):
                              </span>
                              <span>
                                - KSh {formData.discountAmount.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm font-bold text-green-600 mt-1">
                              <span>Amount to Pay:</span>
                              <span>
                                KSh{" "}
                                {(
                                  totalFee - formData.discountAmount
                                ).toLocaleString()}
                              </span>
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    This total will be assigned as the student's fee structure
                  </p>
                </div>
              )}

              {/* Discount/Scholarship Section */}
              {!isEditing && formData.courses.length > 0 && (
                <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h5 className="text-sm font-medium text-purple-900 mb-3 flex items-center gap-2">
                    🎓 Apply Scholarship / Discount (Optional)
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount Amount (KSh)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={totalFee}
                        step="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={formData.discountAmount || 0}
                        onChange={(e) => {
                          const amount = Math.min(
                            totalFee,
                            Math.max(0, Number(e.target.value)),
                          );
                          const percentage =
                            totalFee > 0 ? (amount / totalFee) * 100 : 0;
                          setFormData((prev) => ({
                            ...prev,
                            discountAmount: amount,
                            discountPercentage: percentage,
                          }));
                        }}
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter amount in KSh to reduce fees (max:{" "}
                        {totalFee.toLocaleString()})
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason (Optional)
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={formData.discountReason || ""}
                        onChange={(e) =>
                          handleChange("discountReason", e.target.value)
                        }
                        placeholder="e.g., Merit scholarship, Financial aid"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Why is this discount being applied?
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Class
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.currentClassId}
                    onChange={(e) =>
                      handleChange("currentClassId", e.target.value)
                    }
                    disabled={isLoadingClasses}
                  >
                    <option value="">Select a class</option>
                    {(() => {
                      // Handle different possible response structures
                      let classes = [];
                      const response = classesData as any;
                      if (response?.classes) {
                        classes = response.classes;
                      } else if (response?.data?.classes) {
                        classes = response.data.classes;
                      } else if (
                        response?.data &&
                        Array.isArray(response.data)
                      ) {
                        classes = response.data;
                      } else if (Array.isArray(response)) {
                        classes = response;
                      }

                      return classes.map((cls: any) => (
                        <option key={cls._id} value={cls._id}>
                          {cls.name} - {cls.grade}{" "}
                          {cls.section && `(${cls.section})`}
                        </option>
                      ));
                    })()}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.departmentId}
                    onChange={(e) =>
                      handleChange("departmentId", e.target.value)
                    }
                    disabled={isLoadingDepartments}
                    required
                  >
                    <option value="">Select a department</option>
                    {(() => {
                      const departments = departmentsData?.data || [];
                      return departments.map((dept: any) => (
                        <option key={dept._id} value={dept._id}>
                          {dept.name} ({dept.code})
                        </option>
                      ));
                    })()}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Assign student to a department for proper organization and
                    admission letter generation
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enrollment Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                    value={formData.enrollmentDate}
                    readOnly
                    title="Enrollment date is automatically set to today"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enrollment date is automatically set to today's date
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    How did you hear about ATIAM College?
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.referralSource?.source || ""}
                    onChange={(e) => {
                      const source = e.target.value as
                        | "banner"
                        | "billboard"
                        | "flyers"
                        | "social_media"
                        | "friend"
                        | "family"
                        | "other";
                      setFormData((prev) => ({
                        ...prev,
                        referralSource: {
                          ...prev.referralSource,
                          source,
                          otherDescription:
                            source !== "other"
                              ? ""
                              : prev.referralSource?.otherDescription || "",
                        },
                      }));
                    }}
                  >
                    <option value="">Select an option</option>
                    <option value="banner">Banner</option>
                    <option value="billboard">Billboard</option>
                    <option value="flyers">Flyers</option>
                    <option value="social_media">Social Media</option>
                    <option value="friend">Friend</option>
                    <option value="family">Family</option>
                    <option value="other">Other</option>
                  </select>
                  {formData.referralSource?.source === "other" && (
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="Please specify..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.referralSource?.otherDescription || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            referralSource: {
                              ...prev.referralSource,
                              otherDescription: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Academic Status - Only show for editing */}
                {isEditing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Academic Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.academicStatus}
                      onChange={(e) =>
                        handleChange("academicStatus", e.target.value)
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                      <option value="graduated">Graduated</option>
                      <option value="transferred">Transferred</option>
                      <option value="dropped">Dropped</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Change the student's academic status. This affects their
                      access to the system.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Parent/Guardian Information Section */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Parent/Guardian Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Father's Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.parentGuardianInfo.fatherName}
                    onChange={(e) =>
                      handleChange(
                        "parentGuardianInfo.fatherName",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mother's Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.parentGuardianInfo.motherName}
                    onChange={(e) =>
                      handleChange(
                        "parentGuardianInfo.motherName",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Guardian Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.parentGuardianInfo.guardianName}
                    onChange={(e) =>
                      handleChange(
                        "parentGuardianInfo.guardianName",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div>
                  <PhoneInput
                    label="Guardian Phone"
                    countryCode={
                      formData.parentGuardianInfo.guardianPhoneCountryCode
                    }
                    phoneNumber={formData.parentGuardianInfo.guardianPhone}
                    onCountryCodeChange={(code) =>
                      handleChange(
                        "parentGuardianInfo.guardianPhoneCountryCode",
                        code,
                      )
                    }
                    onPhoneNumberChange={(number) =>
                      handleChange("parentGuardianInfo.guardianPhone", number)
                    }
                    placeholder="712345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Guardian Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.parentGuardianInfo.guardianEmail}
                    onChange={(e) =>
                      handleChange(
                        "parentGuardianInfo.guardianEmail",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emergency Contact Name *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.parentGuardianInfo.emergencyContact.name}
                    onChange={(e) =>
                      handleChange(
                        "parentGuardianInfo.emergencyContact.name",
                        e.target.value,
                      )
                    }
                    required
                  />
                </div>

                <div>
                  <PhoneInput
                    label="Emergency Contact Phone *"
                    countryCode={
                      formData.parentGuardianInfo.emergencyContact
                        .phoneCountryCode
                    }
                    phoneNumber={
                      formData.parentGuardianInfo.emergencyContact.phone
                    }
                    onCountryCodeChange={(code) =>
                      handleChange(
                        "parentGuardianInfo.emergencyContact.phoneCountryCode",
                        code,
                      )
                    }
                    onPhoneNumberChange={(number) =>
                      handleChange(
                        "parentGuardianInfo.emergencyContact.phone",
                        number,
                      )
                    }
                    required={true}
                    placeholder="712345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emergency Contact Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.parentGuardianInfo.emergencyContact.email}
                    onChange={(e) =>
                      handleChange(
                        "parentGuardianInfo.emergencyContact.email",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={
                      formData.parentGuardianInfo.emergencyContact.relationship
                    }
                    onChange={(e) =>
                      handleChange(
                        "parentGuardianInfo.emergencyContact.relationship",
                        e.target.value,
                      )
                    }
                  >
                    <option value="Guardian">Guardian</option>
                    <option value="Father">Father</option>
                    <option value="Mother">Mother</option>
                    <option value="Uncle">Uncle</option>
                    <option value="Aunt">Aunt</option>
                    <option value="Grandparent">Grandparent</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end pt-6 border-t mt-6 gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {isEditing ? "Updating..." : "Creating..."}
                  </div>
                ) : isEditing ? (
                  "Update Student"
                ) : (
                  "Create Student"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Camera Modal */}
      <CameraModal
        isOpen={isCameraModalOpen}
        onClose={() => setIsCameraModalOpen(false)}
        onCapture={handleCameraCapture}
        disableEditing={true}
      />

      {/* Image Crop Modal */}
      {isCropModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Crop Image</h3>
              <button
                onClick={handleCropCancel}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4 flex justify-center">
              {imageSrc && (
                <div className="flex flex-col items-center space-y-4">
                  <ReactCrop
                    crop={crop}
                    onChange={setCrop}
                    onComplete={setCompletedCrop}
                    aspect={1}
                    minWidth={100}
                    minHeight={100}
                    keepSelection={true}
                    ruleOfThirds={true}
                  >
                    <img
                      ref={imgRef}
                      src={imageSrc}
                      alt="Crop preview"
                      className="max-w-full max-h-96 object-contain border border-gray-300"
                      onLoad={() => {
                        // Set initial crop to center square only if crop is still at default position
                        if (imgRef.current && crop.x === 50 && crop.y === 50) {
                          const { width, height } = imgRef.current;
                          const size = Math.min(width, height) * 0.8; // 80% of the smaller dimension
                          const x = (width - size) / 2;
                          const y = (height - size) / 2;
                          setCrop({
                            unit: "px",
                            width: size,
                            height: size,
                            x,
                            y,
                          });
                        }
                      }}
                    />
                  </ReactCrop>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleCropCancel}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCropComplete}
                disabled={!completedCrop}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentModal;
